import React, { createContext, useState, useEffect } from 'react';

export const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
    const [location, setLocation] = useState(null);
    const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
    const [locationError, setLocationError] = useState(null);

    // 🔹 Parser merged here
    const parseAddressComponents = (result, lat, lng) => {
        const components = result.address_components;
        const geometry = result.geometry;

        let name = "";
        let area = "";
        let city = "";
        let state = "";
        let countryCode = "";
        let pincode = "";

        components.forEach((comp) => {
            if (comp.types.includes("premise") || comp.types.includes("street_number")) {
                name = comp.long_name;
            } else if (comp.types.includes("route")) {
                area = comp.long_name;
            } else if (
                comp.types.includes("sublocality") ||
                comp.types.includes("sublocality_level_1")
            ) {
                if (!area) {
                    area = comp.long_name;
                } else {
                    area = `${area}, ${comp.long_name}`;
                }
            } else if (comp.types.includes("locality")) {
                city = comp.long_name;
            } else if (comp.types.includes("administrative_area_level_1")) {
                state = comp.long_name;
            } else if (comp.types.includes("country")) {
                countryCode = comp.short_name;
            } else if (comp.types.includes("postal_code")) {
                pincode = comp.long_name;
            }
        });

        return {
            name,
            address: result.formatted_address,
            area,
            city,
            state,
            countryCode,
            lat: lat || geometry.location.lat(),
            lng: lng || geometry.location.lng(),
            pincode,
            timestamp: new Date().toISOString(), // ⏱️ added
        };
    };

    const fetchAddressFromLatLng = async (lat, lng) => {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4`
            );
            const data = await response.json();

            if (data.status === "OK" && data.results.length > 0) {
                const result = data.results[0];
                const newLocation = parseAddressComponents(result, lat, lng);

                setLocation(newLocation);
                setIsLocationModalOpen(false);

                localStorage.setItem("userLocation", JSON.stringify(newLocation));

                // 🔄 Reload page after saving
                window.location.reload();
            } else {
                setLocationError("Unable to fetch address details.");
                setIsLocationModalOpen(true);
            }
        } catch (error) {
            setLocationError("Error fetching location details.");
            setIsLocationModalOpen(true);
        }
    };

    // Initial location check
    useEffect(() => {
        const savedLocation = localStorage.getItem("userLocation");
        if (savedLocation) {
            setLocation(JSON.parse(savedLocation));
            setIsLocationModalOpen(false);
        } else {
            setLocation(null);
            setIsLocationModalOpen(true);

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const { latitude: lat, longitude: lng } = position.coords;
                        fetchAddressFromLatLng(lat, lng);
                    },
                    (error) => {
                        setLocationError("Error getting location: " + error.message);
                        setIsLocationModalOpen(true);
                    },
                    { enableHighAccuracy: true, timeout: 10000 }
                );
            } else {
                setLocationError("Geolocation not supported.");
                setIsLocationModalOpen(true);
            }
        }
    }, []);

    // Listen for manual localStorage changes
    useEffect(() => {
        const handleStorageChange = () => {
            const savedLocation = localStorage.getItem("userLocation");
            if (!savedLocation) {
                setLocation(null);
                setIsLocationModalOpen(true);
            } else {
                setLocation(JSON.parse(savedLocation));
                setIsLocationModalOpen(false);
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    return (
        <LocationContext.Provider
            value={{
                location,
                setLocation,
                fetchAddressFromLatLng,
                isLocationModalOpen,
                setIsLocationModalOpen,
                locationError,
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};
